import { yupResolver } from '@hookform/resolvers/yup';
import {
  Box,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Grid,
  Link,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { useGA4React } from 'ga-4-react';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';
import * as yup from 'yup';
import { objectToArray } from '../../libs/helpers';
import { Report } from '../../libs/loggers';
import PrimaryButton from '../ui/buttons/primaryButton';
import Input from '../ui/forms/fields/input';

function ContactInfo({
  extendedForm,
  data,
  submitionData,
  onNext,
  onBack,
  onSaveAndExit,
  stepSlug,
  openSave,
  isRaHelp,
}) {
  const loading = useSelector((state) => state.submition.submition?.loading);
  const userId = useSelector((state) => state.submition.data?.userId);
  const page = window.location.pathname.replace(/^\/|\/$/g, '');
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down('sm'));
  const submitionId = useSelector((state) => state.submition.submition?.submitionId);
  const formStepper = useSelector((state) => state.formStepper.value);

  const ContactInfoFormValidationSchema = (data, extendedForm, isRaHelp) => {
    let baseValidationSchema = yup.object().shape({
      stepSlug: yup.string().required(),
      title: yup.string().required('Please add your title.'),
      firstName: yup.string().required('Please complete your First Name'),
      surname: yup.string().required('Please complete your Surname'),
      telHome: yup.string(),
      telWork: yup.string(),
      cell: yup
        .string()
        .trim()
        .required('What is your cellphone number?')
        .matches(/^\+?[0-9 ]+$/, 'Please enter a valid cellphone number.'),
      fax: yup.string(),
      email: yup
        .string()
        .email('Please enter a valid email address.')
        .required(
          'Please provide an email address, we will be sending your application to this email address.'
        ),
      confirmEmail: yup
        .string()
        .email('Please enter a valid email address.')
        .required('Please confirm email address.')
        .oneOf([yup.ref('email'), null], 'Emails must match, please revise and correct.'),
      preferPost: yup.bool().default(false),
    });

    return baseValidationSchema;
  };

  const {
    register,
    handleSubmit,
    getValues,
    control,
    setValue,
    formState: { errors },
  } = useForm({
    mode: 'onBlur',
    resolver: yupResolver(ContactInfoFormValidationSchema(data, extendedForm, isRaHelp)),
  });
  let ga4React = useGA4React();

  useEffect(() => {
  setValue('stepSlug', stepSlug, { shouldValidate: true });
}, [stepSlug, setValue]);

  useEffect(() => {
    if (window.innerWidth <= 768) {
      window.scrollTo(0, 0);
    }
  }, []);

  useEffect(() => {
    if (isRaHelp) {
      setValue('SELF', 'Yes');
    }
  }, [isRaHelp, setValue]);

  useEffect(() => {
    if (submitionData) {
      Object.entries(submitionData).forEach(([key, value]) => {
        setValue(key, value, { validate: true });
      });
    }
  }, [submitionData]);

  useEffect(() => {
    const eventInfo = {
      action: 'view',
      category: 'step4_formstep_view',
      description: 'viewed a form step in step 4',
      value: {
        currentPage: page,
        stepSlug,
        activeFormStep: formStepper?.step,
      },
    };

    Report(eventInfo, userId, submitionId);
  }, []);

  const NavButtons = (props) => (
    <>
      <Box display="flex" justifyContent={'flex-end'}>
        <PrimaryButton disabled={props.isFirstStep} onClick={() => onBack()} sx={{ mt: 1, mr: 1 }}>
          Back
        </PrimaryButton>
        <PrimaryButton
          onClick={() => handleNext()}
          disabled={loading}
          sx={{
            mt: 1,
            mr: 1,
            '&.Mui-disabled': {
              background: '#CC6323',
              color: '#fff',
            },
          }}
        >
          {userId ? 'Save & Continue' : 'Continue'}{' '}
          {loading && <CircularProgress sx={{ ml: 1 }} size={isMobile ? 15 : 20} color="inherit" />}
        </PrimaryButton>
        <PrimaryButton variant="outlined" onClick={() => handleSaveAndExit(userId)} sx={{ mt: 1 }}>
          {'Save & Exit'}
        </PrimaryButton>
      </Box>
      {!userId && (
        <Box display="flex" justifyContent={'flex-end'} marginTop={1}>
          <Typography variant="body1">
            {' '}
            Your progress is not being saved. Click{' '}
            <Link
              sx={{ cursor: 'pointer' }}
              variant="body1"
              underline="always"
              onClick={() => {
                handleOpenSave();
              }}
            >
              here
            </Link>{' '}
            to save.
          </Typography>
        </Box>
      )}
    </>
  );

  const handleNext = async () => {
    ga4React.event('complete_form_step', `${stepSlug}`, 'investement_submition');
    setValue('completeProcess', false, { shouldValidate: true });
    handleSubmit(onSubmit)();
  };

  const handleSaveAndExit = (userId) => {
    const data = getValues();
    const newSubmition = { ...submitionData, ...data };
    onSaveAndExit(newSubmition, userId);
  };

  const handleOpenSave = () => {
    const data = getValues();
    const newSubmition = { ...submitionData, ...data };
    openSave(newSubmition);
  };

  const onSubmit = (data) => {
    const newSubmition = { ...submitionData, ...data };
    onNext(newSubmition);
  };

  return (
    <Grid container spacing={1}>
      <Box>
        <Typography mt={1}>
          ** Please provide us with your contact details/details of the investor (if applying on
          behalf of someone else)
        </Typography>
      </Box>
      <Grid item xs={12}>
        <Grid container spacing={1}>
          <Grid item xs={12} md={6}>
            <Input
              required
              label="Title"
              {...register('title')}
              error={!!errors.title}
              helperText={errors?.title?.message}
            />

            <Input
              label="First Names"
              {...register('firstName')}
              required
              error={!!errors.firstName}
              helperText={errors?.firstName?.message}
            />
            <Input
              label="Surname"
              {...register('surname')}
              required
              error={!!errors.surname}
              helperText={errors?.surname?.message}
            />

            <Input
              label="Email Address"
              {...register('email')}
              error={!!errors.email}
              required
              helperText={
                errors.email?.message ||
                'We will be sending your application to this email address for signing.'
              }
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Input
              label="Confirm Email Address"
              {...register('confirmEmail')}
              required
              error={!!errors.confirmEmail}
              helperText={errors?.confirmEmail?.message}
              onPaste={(e) => e.preventDefault()}
              autoComplete="off"
            />

            <Input
              label="Cellphone Number"
              {...register('cell')}
              required
              error={!!errors.cell}
              helperText={errors?.cell?.message}
            />
            <Input
              label="Home Telephone"
              {...register('telHome')}
              error={!!errors.telHome}
              helperText={errors?.telHome?.message}
            />
            <Input label="Work Telephone" {...register('telWork')} />
            <Input label="Fax Number" {...register('fax')} />
          </Grid>
          <Grid item xs={12}>
            <Typography>
              All communication will be sent to you via e-mail. If you prefer post or do not have an
              e-mail address, please check below.
            </Typography>
            <FormControlLabel
              control={
                <Checkbox {...register('preferPost')} name={'preferPost'} control={control} />
              }
              label={'I prefer post | I do not have a email address.'}
            />
          </Grid>
        </Grid>
      </Grid>
      <Grid item xs={12} md={12}>
        <NavButtons isLastStep={false} isFirstStep={true} index={0} />
      </Grid>
      <Box display={'inlineBlock'}>
        {objectToArray(errors).map((error, index) => (
          <Typography color={'error'} key={index}>
            {error.value.message}
          </Typography>
        ))}
      </Box>
    </Grid>
  );
}

export default ContactInfo;
